import type { PropTypes } from './TopUserCard.types';
import type { ReactElement } from 'react';

import { Badge, Box, Card, Flex, Heading, Separator, Text } from '@rango-dev/whale';
import { CircleDollarSign, CircleStar } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAppSelector } from '../../../hooks/reduxHooks';
import { useWindowDimensions } from '../../../hooks/useWindowDimensions';
import { Avatar } from '../../Profile/Avatar';
import { shortenUsername } from '../../Profile/Leaderboard/Leaderboard.helpers';
import {
  displayUsdValue,
  displayUserRank,
  displayUserRankOrdinal,
} from '../../Profile/ProfileSummary/ProfileSummary.helpers';

import styles from './TopUserCard.module.css';

const SCORE_ICON_SIZE = 24;
const SCORE_ICON_SIZE_MOBILE = 20;

export function TopUserCard(props: PropTypes): ReactElement {
  const { user, type } = props;
  const { isMobile } = useWindowDimensions();
  const { t } = useTranslation();
  const { data: profileDetails } = useAppSelector((state) => state.profile.profileDetails);
  const isCurrentUser = profileDetails?.id === user.id;

  const scoreIconSize = isMobile ? SCORE_ICON_SIZE_MOBILE : SCORE_ICON_SIZE;

  const scoreNode =
    type === 'referral' ? displayUsdValue(user.referralVolume) : displayUserRank(user.score);
  const ScoreIcon = type === 'referral' ? CircleDollarSign : CircleStar;
  const scoreIconColor = type === 'referral' ? 'var(--red-9)' : 'var(--orange-9)';

  return (
    <Card variant="surface" size="3" className={styles.card} asChild>
      <Link id="leader-board-item-link" to={`../profile/${user.username}`} className={styles.link}>
        <Flex direction="column" gap="3" width="100%">
          <Flex justify="between" align="start" width="100%">
            <Flex direction="column" gap="0" align="center">
              <Heading size="7" weight="bold">
                {displayUserRankOrdinal(user.rank)}
              </Heading>
              <Text size="2" color="gray">
                {t('Rank')}
              </Text>
            </Flex>
            <Box position="relative">
              <Avatar username={user.username} size="5" />
              {type === 'score' && (
                <Badge
                  color="teal"
                  variant="solid"
                  radius="medium"
                  size="1"
                  className={styles.levelBadge}>
                  {user.level}
                </Badge>
              )}
            </Box>
          </Flex>
          <Separator size="4" />
          <Flex justify="between" align="center" width="100%" gap="2">
            <Flex align="center" gap="2" className={styles.usernameWrap}>
              <Text size="3" weight="medium" truncate className={styles.username}>
                {shortenUsername({ username: user.username, startChars: 10, endChars: 5 })}
              </Text>
              {isCurrentUser && (
                <Badge variant="soft" color="cyan" size="1">
                  {t('You')}
                </Badge>
              )}
            </Flex>
            <Flex align="center" gap="1" flexShrink="0">
              <ScoreIcon size={scoreIconSize} color={scoreIconColor} />
              <Text size="2" weight="medium">
                {scoreNode}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Link>
    </Card>
  );
}
